/**
 * Theme system entry point
 * Combines all theme tokens into cohesive light and dark themes
 */

import {
  lightColors,
  darkColors,
  africaBetLightColors,
  africaBetDarkColors,
  type ColorPalette
} from './colors';
import { typography, type Typography } from './typography';
import { spacing, type Spacing } from './spacing';
import { effects, type Effects } from './effects';
import { breakpoints, type Breakpoints } from './breakpoints';
import type { BrandType, ThemeMode } from '@/Utils/params-manager';

export interface Theme {
  colors: ColorPalette;
  typography: Typography;
  spacing: Spacing;
  borderRadius: Effects['borderRadius'];
  shadows: Effects['shadows'];
  transitions: Effects['transitions'];
  zIndex: Effects['zIndex'];
  breakpoints: Breakpoints;
}

/**
 * Base theme with shared properties
 */
const baseTheme = {
  typography,
  spacing,
  ...effects,
  breakpoints,
} as const;

/**
 * Light theme
 */
export const lightTheme: Theme = {
  ...baseTheme,
  colors: lightColors,
};

/**
 * Dark theme
 */
export const darkTheme: Theme = {
  ...baseTheme,
  colors: darkColors,
};

export const africaBetLightTheme: Theme = {
  ...baseTheme,
  colors: africaBetLightColors,
};

export const africaBetDarkTheme: Theme = {
  ...baseTheme,
  colors: africaBetDarkColors,
};

export const getTheme = (mode: ThemeMode, brand: BrandType): Theme => {
  if (brand === 'africabet') {
    return mode === 'dark' ? africaBetDarkTheme : africaBetLightTheme;
  }

  return mode === 'dark' ? darkTheme : lightTheme;
};

/**
 * Default theme (light)
 */
export const theme = lightTheme;

/**
 * Type exports for TypeScript support
 */
export type { ColorPalette, Typography, Spacing, Effects, Breakpoints };
export {
  lightColors,
  darkColors,
  africaBetLightColors,
  africaBetDarkColors,
  typography,
  spacing,
  effects,
  breakpoints
};
